import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ShoppingBag, Check, X, Play, Loader2, RefreshCw, Cake } from "lucide-react";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

const RES_STATUS = {
  active: { label: "Ativa", c: "bg-green-100 text-green-700" },
  picked_up: { label: "Retirado", c: "bg-blue-100 text-blue-700" },
  cancelled: { label: "Cancelada", c: "bg-red-100 text-red-700" },
  expired: { label: "Expirada", c: "bg-slate-200 text-slate-600" },
};
const LOG_TYPE = {
  followup: "Retomar conversa",
  os_reminder: "Lembrete de OS",
  ready_notice: "OS pronta",
  checkin: "Pós-atendimento",
  away: "Fora do horário",
  opt: "SAIR / VOLTAR",
  birthday: "Aniversário",
  review: "Avaliação Google",
};
const COUPON_STATUS = {
  active: "bg-green-100 text-green-700",
  used: "bg-blue-100 text-blue-700",
};

function Coupons({ reloadKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/coupons").then((r) => setItems(r.data)); }, [reloadKey]);
  const label = (c) => (c.status === "used" ? "Usado" : c.expires_at < new Date().toISOString() ? "Expirado" : "Ativo");
  return (
    <section className="bg-white border border-slate-200 rounded-sm" data-testid="coupons-section">
      <h2 className="font-display text-xl font-bold p-4 border-b border-slate-100 flex items-center gap-2"><Cake size={18} className="text-[#2563EB]" /> Cupons de aniversário</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>{["Código", "Cliente", "Desconto", "Válido até", "Status"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`coupon-row-${c.code}`}>
                <td className="p-3 font-mono font-semibold">{c.code}</td>
                <td className="p-3">{c.customer_name}<div className="text-xs text-slate-500">{c.customer_phone}</div></td>
                <td className="p-3">{c.percent}%</td>
                <td className="p-3 text-xs">{dt(c.expires_at)}</td>
                <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-sm ${COUPON_STATUS[c.status] || "bg-slate-200 text-slate-600"}`}>{label(c)}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500" data-testid="coupons-empty">Nenhum cupom gerado ainda.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Reservations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const load = () => api.get("/reservations", { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = async (r, status) => {
    if (status === "cancelled" && !window.confirm(`Cancelar a reserva de ${r.product_name}?`)) return;
    try {
      await api.patch(`/reservations/${r.id}`, { status });
      toast.success(status === "picked_up" ? "Marcado como retirado — registre a venda no PDV" : "Reserva cancelada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-sm" data-testid="reservations-section">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-100">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><ShoppingBag size={18} className="text-[#2563EB]" /> Reservas da IA</h2>
        <div className="flex gap-1">
          {[["active", "Ativas"], ["", "Todas"]].map(([k, l]) => (
            <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`reservations-filter-${k || "all"}`}
              className={`px-3 py-1 text-xs font-semibold rounded-sm ${filter === k ? "bg-[#0A1628] text-white" : "bg-slate-100 hover:bg-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>{["Produto", "Qtd", "Valor", "Cliente", "Criada", "Vence", "Status", ""].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`reservation-row-${r.id}`}>
                <td className="p-3 font-medium">{r.product_name}<div className="text-[10px] text-slate-400 font-mono">#{r.id.slice(0, 6).toUpperCase()}</div></td>
                <td className="p-3">{r.quantity}</td>
                <td className="p-3 font-semibold">{brl(r.price * r.quantity)}</td>
                <td className="p-3">{r.customer_name}<div className="text-xs text-slate-500">{r.customer_phone}</div></td>
                <td className="p-3 text-xs">{dt(r.created_at)}</td>
                <td className="p-3 text-xs">{dt(r.expires_at)}</td>
                <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-sm ${RES_STATUS[r.status]?.c}`}>{RES_STATUS[r.status]?.label}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  {r.status === "active" && (
                    <>
                      <button onClick={() => close(r, "picked_up")} data-testid={`reservation-pickup-${r.id}`}
                        className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded-sm mr-1 inline-flex items-center gap-1"><Check size={12} /> Retirado</button>
                      <button onClick={() => close(r, "cancelled")} data-testid={`reservation-cancel-${r.id}`}
                        className="text-xs border border-red-300 text-red-600 hover:bg-red-50 px-2 py-1 rounded-sm inline-flex items-center gap-1"><X size={12} /> Cancelar</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-slate-500" data-testid="reservations-empty">Nenhuma reserva.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AutomationLog({ reloadKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/automations/log").then((r) => setItems(r.data)); }, [reloadKey]);
  return (
    <section className="bg-white border border-slate-200 rounded-sm" data-testid="automation-log-section">
      <h2 className="font-display text-xl font-bold p-4 border-b border-slate-100">Mensagens automáticas enviadas</h2>
      <div className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
        {items.map((m) => (
          <div key={m.id} className="p-3 text-sm flex gap-3" data-testid={`automation-log-${m.id}`}>
            <div className="w-36 shrink-0">
              <span className="text-[10px] uppercase font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">{LOG_TYPE[m.msg_type] || m.msg_type}</span>
              <div className="text-xs text-slate-500 mt-1">{m.phone}</div>
              <div className="text-[10px] text-slate-400">{dt(m.created_at)}</div>
            </div>
            <div className="whitespace-pre-wrap text-slate-700">{m.content}</div>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-center text-slate-500" data-testid="automation-log-empty">Nenhuma mensagem automática ainda.</div>}
      </div>
    </section>
  );
}

function OwnerNotifications({ reloadKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/whatsapp/owner-notifications").then((r) => setItems(r.data)); }, [reloadKey]);
  return (
    <section className="bg-white border border-slate-200 rounded-sm" data-testid="owner-notifications-section">
      <h2 className="font-display text-xl font-bold p-4 border-b border-slate-100">Avisos enviados para o seu WhatsApp</h2>
      <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
        {items.map((n) => (
          <div key={n.id} className="p-3 text-sm flex gap-3" data-testid={`owner-notification-${n.id}`}>
            <div className="w-36 shrink-0">
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm ${n.ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{n.ok ? "Enviado" : "Falhou"}</span>
              <div className="text-[10px] text-slate-400 mt-1">{dt(n.at)}</div>
            </div>
            <div className="whitespace-pre-wrap text-slate-700">{n.message}{!n.ok && <div className="text-xs text-red-600 mt-1">{n.error}</div>}</div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-slate-500" data-testid="owner-notifications-empty">Nenhum aviso enviado ainda.</div>}
      </div>
    </section>
  );
}

export default function Automations() {
  const [running, setRunning] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/automations/run-now");
      if (data.quiet_or_unconfigured) toast.info("Horário silencioso (21h–8h): só as reservas vencidas foram verificadas.");
      else toast.success(`Retomadas: ${data.followups} · Lembretes: ${data.os_reminders} · Pós-atendimento: ${data.checkins} · Aniversários: ${data.birthdays} · Avaliações: ${data.reviews} · Reservas vencidas: ${data.reservations_expired}`);
      setReloadKey((k) => k + 1);
    } catch { toast.error("Erro ao rodar automações"); } finally { setRunning(false); }
  };
  return (
    <div className="space-y-6" data-testid="automations-page">
      <header className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">IA no WhatsApp</div>
          <h1 className="font-display text-4xl font-bold">Reservas e Automações</h1>
          <p className="text-sm text-slate-500 mt-1">As automações rodam sozinhas a cada 15 minutos. Ligue ou desligue cada uma em Configurações.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setReloadKey((k) => k + 1)} data-testid="automations-refresh" className="border border-slate-300 hover:bg-slate-100 px-3 py-2 rounded-sm"><RefreshCw size={16} /></button>
          <button onClick={runNow} disabled={running} data-testid="automations-run-now"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
            {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Rodar agora
          </button>
        </div>
      </header>
      <Reservations key={`r-${reloadKey}`} />
      <Coupons reloadKey={reloadKey} />
      <OwnerNotifications reloadKey={reloadKey} />
      <AutomationLog reloadKey={reloadKey} />
    </div>
  );
}
